import { html, nothing } from 'lit';
import { SdsMultiselect } from './sds-multiselect.lit';
import { SdsInputMultiselect, } from '../../input/multiselect/sds-input-multiselect.lit';
import { cleanMenuItemsFocus, focusNextItem, focusPreviousItem, initMenuItemsFocus, scrollFocusedItemIfNeeded, scrollToSelected, } from '../menu-items-state-handler';
import { isNil } from 'lodash-es';
import { SdsIcon } from '../../icon/sds-icon.lit';
export class MultiselectRenderer {
    constructor(sdsMultiselect) {
        this._selectionAction = '';
        this.handleClear = () => {
            this.resetValue();
            this._sdsMultiselect.dispatchEvent(new CustomEvent(SdsMultiselect.CLEARED_EVENT, {
                detail: {
                    identifiers: new Set(this._sdsMultiselect.selectedItems.map(item => item.identifier || '')),
                },
                bubbles: false,
                composed: false,
            }));
            this._sdsMultiselect.dispatchChangeEvent();
            this._sdsMultiselect.focused = true;
            this.resetScroll();
            cleanMenuItemsFocus(this._sdsMultiselect._contextMenu);
            initMenuItemsFocus(this._sdsMultiselect._contextMenu);
            this._sdsMultiselect.syncSelectAllState();
        };
        this.handleCaret = () => {
            if (this._sdsMultiselect._contextMenu) {
                this._sdsMultiselect._contextMenu.opened ? this.hideMenu(true) : this.openMenu();
            }
        };
        this.handleInputClick = () => {
            if (this._sdsMultiselect._contextMenu && !this._sdsMultiselect._contextMenu.opened) {
                this.openMenu();
            }
            else {
                this._sdsMultiselect.inputComponent.cleanLabelsFocus();
            }
        };
        this.handleInput = (event) => {
            this.handleInputChange(event);
            this._sdsMultiselect.dispatchInputEvent();
            if (this._sdsMultiselect.inputValue !== '') {
                this.openMenu();
            }
        };
        this.handleInputChange = (event) => {
            this._sdsMultiselect.selectItemStateHandler(item => {
                item.focused = false;
            });
            if (this._sdsMultiselect.selectAllItem) {
                this._sdsMultiselect.selectAllItem.focused = false;
            }
            this._sdsMultiselect.inputValue = event.detail.value;
            scrollToSelected(this._sdsMultiselect._contextMenu);
            this._sdsMultiselect.inputComponent.cleanLabelsFocus();
        };
        this.handleLabelClose = (event) => {
            const identifier = event.detail.identifier;
            if (identifier) {
                this.closeLabel(identifier);
            }
            this._sdsMultiselect.focused = true;
        };
        this.handleKeyDown = (event) => {
            var _a, _b;
            const isLTR = (document.documentElement.getAttribute('dir') || 'ltr') === 'ltr';
            switch (event.code) {
                case 'Escape':
                    this.hideMenu(true); // Focus input when closed via ESC key
                    event.stopPropagation();
                    break;
                case 'Tab':
                    // Only intercept Tab when menu is open, otherwise allow normal tab navigation
                    if ((_a = this._sdsMultiselect._contextMenu) === null || _a === void 0 ? void 0 : _a.opened) {
                        this.hideMenu(true); // Focus input when closed via Tab key (menu open)
                        event.stopPropagation();
                    }
                    break;
                case 'Enter':
                case 'NumpadEnter':
                    if (this._sdsMultiselect.inputComponent.hasFocusedLabel()) {
                        this.handleDeleteKeydown();
                        event.stopPropagation();
                    }
                    else {
                        this._sdsMultiselect.handleEnterKeydown(event);
                        event.stopPropagation();
                    }
                    break;
                case 'Delete':
                case 'Backspace':
                    this.handleDeleteKeydown();
                    event.stopPropagation();
                    break;
                case 'ArrowUp':
                    this.handleArrowUp();
                    event.stopPropagation();
                    event.preventDefault();
                    break;
                case 'ArrowDown':
                    if (!((_b = this._sdsMultiselect._contextMenu) === null || _b === void 0 ? void 0 : _b.opened)) {
                        event.preventDefault();
                        event.stopPropagation();
                        this.openMenu();
                    }
                    else {
                        this.handleArrowDown();
                        event.stopPropagation();
                        event.preventDefault();
                    }
                    break;
                case 'ArrowLeft':
                    if (isLTR) {
                        this.handleArrowLeft();
                    }
                    else {
                        this.handleArrowRight();
                    }
                    event.stopPropagation();
                    break;
                case 'ArrowRight':
                    if (isLTR) {
                        this.handleArrowRight();
                    }
                    else {
                        this.handleArrowLeft();
                    }
                    event.stopPropagation();
                    break;
            }
        };
        this.handleArrowLeft = () => {
            this._sdsMultiselect.inputComponent.shiftLabelFocusLeft();
            cleanMenuItemsFocus(this._sdsMultiselect._contextMenu);
        };
        this.handleArrowRight = () => {
            this._sdsMultiselect.inputComponent.shiftLabelFocusRight();
            cleanMenuItemsFocus(this._sdsMultiselect._contextMenu);
        };
        this.calculateMenuSize = () => {
            const inputWidth = this._sdsMultiselect.getBoundingClientRect().width;
            return `${inputWidth}px`;
        };
        this.openMenu = () => {
            var _a;
            const target = this._sdsMultiselect.trigger;
            if (target instanceof SdsInputMultiselect &&
                this._sdsMultiselect._contextMenu &&
                !this._sdsMultiselect._contextMenu.opened) {
                const trigger = (_a = target.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('.sds-input-multiselect__wrapper');
                this._sdsMultiselect._contextMenu.width = this.calculateMenuSize();
                this._sdsMultiselect._contextMenu.show(trigger);
                this._sdsMultiselect.inputComponent.opened = true;
                this._sdsMultiselect.focused = true;
                this._sdsMultiselect.addEventListeners();
                initMenuItemsFocus(this._sdsMultiselect._contextMenu);
                scrollToSelected(this._sdsMultiselect._contextMenu);
                // Set initial ariaActiveDescendantElement to the focused item
                setTimeout(() => {
                    this._sdsMultiselect.setInitialAriaActiveDescendant();
                });
                this._sdsMultiselect.dispatchOpenEvent();
                this._sdsMultiselect.syncSelectAllState();
            }
        };
        this._sdsMultiselect = sdsMultiselect;
    }
    render() {
        var _a;
        const noData = this._sdsMultiselect.menuItems.length === 0 ||
            this._sdsMultiselect.menuItems.every(item => item.style.display === 'none');
        return html `
            <div class="sds-multiselect-container">
                <sds-input-multiselect
                    id="trigger"
                    .placeholder="${this._sdsMultiselect.placeholder}"
                    .value="${this._sdsMultiselect.inputValue}"
                    .hint="${this._sdsMultiselect.hint}"
                    .errorMessage="${this._sdsMultiselect.errorMessage}"
                    searchable
                    ?invalid="${this._sdsMultiselect.invalid}"
                    ?disabled="${this._sdsMultiselect.disabled}"
                    ?focused="${this._sdsMultiselect.focused}"
                    ?opened="${(_a = this._sdsMultiselect._contextMenu) === null || _a === void 0 ? void 0 : _a.opened}"
                    @sds.input.multiselect.cleared="${this.handleClear}"
                    @sds.input.multiselect.caret.changed="${this.handleCaret}"
                    @sds.input.multiselect.input.clicked="${this.handleInputClick}"
                    @sds.input="${this.handleInput}"
                    @sds.input.changed="${this.handleInputChange}"
                    @sds.input.multiselect.label.closed="${this.handleLabelClose}"
                    @keydown="${this.handleKeyDown}"
                ></sds-input-multiselect>

                ${this.renderSelectionAction()}

                <sds-context-menu
                    id="multiselect-menu"
                    width="${this.calculateMenuSize()}"
                    offset="1"
                >
                    ${this._sdsMultiselect.renderSelectAll()}
                    <slot @slotchange="${this._sdsMultiselect.onContentChange}"></slot>
                    ${noData
            ? html `<sds-cm-select-item
                              disabled
                              heading
                              name="No Data"
                              role="option"
                              aria-disabled="true"
                          ></sds-cm-select-item>`
            : nothing}
                </sds-context-menu>
            </div>
        `;
    }
    willUpdate(_changedProperties) {
        if (_changedProperties.has('inputValue')) {
            this._sdsMultiselect.updateMenuContent();
            if (this._sdsMultiselect.inputValue === '' && this._sdsMultiselect._contextMenu) {
                scrollToSelected(this._sdsMultiselect._contextMenu);
            }
        }
    }
    resetValue() {
        this.resetInputValue();
        this._sdsMultiselect.selectedItems = [];
        this._sdsMultiselect.inputComponent.clearLabels();
        this._sdsMultiselect.selectItemStateHandler(item => {
            item.setSelectedSilently(false);
            item.focused = false;
        });
        // Update _value to match selectedItems (empty array)
        this._sdsMultiselect.updateValueFromSelectedItems();
    }
    resetInputValue() {
        this._sdsMultiselect.inputComponent.value = '';
        this._sdsMultiselect.inputValue = '';
        this._sdsMultiselect.dispatchInputEvent();
    }
    getLabelMetadata(item) {
        var _a, _b;
        let labelMetadata = {
            name: item.name,
            withAvatar: item.withAvatar,
            focused: false,
        };
        if (item.withAvatar) {
            const avatar = (_a = item.avatar) === null || _a === void 0 ? void 0 : _a.pop();
            if (avatar) {
                labelMetadata = Object.assign(Object.assign({}, labelMetadata), { name: avatar.name, src: avatar.src, role: avatar.role, icon: avatar.icon });
            }
        }
        else if (item.withIcon) {
            const icon = (_b = item.icon) === null || _b === void 0 ? void 0 : _b.pop();
            if (!isNil(icon) && icon instanceof SdsIcon) {
                labelMetadata = Object.assign(Object.assign({}, labelMetadata), { icon: icon.name });
            }
            else if (!isNil(icon)) {
                labelMetadata = Object.assign(Object.assign({}, labelMetadata), { icon: icon.icon });
            }
        }
        return labelMetadata;
    }
    setSelectionAction(action) {
        this._selectionAction = action;
        this._sdsMultiselect.requestUpdate();
    }
    renderSelectionAction() {
        if (this._selectionAction) {
            // Clear the action after rendering to prevent re-announcement
            setTimeout(() => {
                this._selectionAction = '';
                this._sdsMultiselect.requestUpdate();
            }, 100);
            return html ` <div class="sr-only" aria-live="polite">${this._selectionAction}</div> `;
        }
    }
    closeLabel(value) {
        this._sdsMultiselect.selectItemStateHandler(item => {
            if (item.value === value) {
                item.setSelectedSilently(false);
            }
        });
        this._sdsMultiselect.inputComponent.deleteLabel(value);
        this._sdsMultiselect.selectedItems = this._sdsMultiselect.selectedItems.filter(item => item.value !== value);
        // Update _value to match selectedItems
        this._sdsMultiselect.updateValueFromSelectedItems();
        this._sdsMultiselect.syncSelectAllState();
        this._sdsMultiselect.dispatchSelectedEvent();
        this._sdsMultiselect.dispatchChangeEvent();
    }
    handleDeleteKeydown() {
        const identifier = this._sdsMultiselect.inputComponent.getFocusedLabelIdentifier();
        if (identifier) {
            this.closeLabel(identifier);
        }
        else {
            this._sdsMultiselect.inputComponent.shiftLabelFocusLeft();
        }
    }
    handleArrowDown() {
        const nextItem = focusNextItem(this._sdsMultiselect._contextMenu);
        if (nextItem) {
            this._sdsMultiselect.updateAriaActiveDescendant(nextItem);
        }
        this._sdsMultiselect.requestUpdate();
        scrollFocusedItemIfNeeded(this._sdsMultiselect._contextMenu);
        this._sdsMultiselect.inputComponent.cleanLabelsFocus();
    }
    handleArrowUp() {
        const nextItem = focusPreviousItem(this._sdsMultiselect._contextMenu);
        if (nextItem) {
            this._sdsMultiselect.updateAriaActiveDescendant(nextItem);
        }
        this._sdsMultiselect.requestUpdate();
        scrollFocusedItemIfNeeded(this._sdsMultiselect._contextMenu);
        this._sdsMultiselect.inputComponent.cleanLabelsFocus();
    }
    resetScroll() {
        this._sdsMultiselect._contextMenu.getMenu().scrollTop = 0;
    }
    hideMenu(focusInput = true) {
        var _a;
        if ((_a = this._sdsMultiselect._contextMenu) === null || _a === void 0 ? void 0 : _a.opened) {
            this._sdsMultiselect._contextMenu.hide();
            this._sdsMultiselect.inputComponent.opened = false;
            this._sdsMultiselect.inputComponent.cleanLabelsFocus();
            this._sdsMultiselect.inputComponent.value = '';
            this._sdsMultiselect.inputValue = '';
            // Clear ariaActiveDescendantElement when menu closes
            this._sdsMultiselect.clearAriaActiveDescendant();
            // Set focused property for styling/state (separate from DOM focus)
            this._sdsMultiselect.focused = focusInput;
            cleanMenuItemsFocus(this._sdsMultiselect._contextMenu);
            this._sdsMultiselect.removeEventListeners();
            this._sdsMultiselect.dispatchCloseEvent();
        }
    }
    firstUpdated() {
        return;
    }
}
